import React, { useState } from 'react';
import { Box, TextField, Typography, Button, IconButton, Paper, Alert } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Help as HelpIcon } from '@mui/icons-material';

import { MethodEditorProps } from '../types';
import { SetContextHelpModal } from '../components/SetContextHelpModal';

export const SetContextEditor: React.FC<MethodEditorProps> = ({ payload, onChange }) => {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);

  const addContextValue = () => {
    if (newKey) {
      onChange({
        ...payload,
        [newKey]: newValue,
      });
      setNewKey('');
      setNewValue('');
    }
  };

  const removeContextValue = (key: string) => {
    const { [key]: removed, ...rest } = payload || {};
    onChange(rest);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle2">Context Values</Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<HelpIcon />}
          onClick={() => setShowHelpModal(true)}
        >
          Need Help?
        </Button>
      </Box>

      {/* Existing context values */}
      {Object.entries(payload || {}).map(([key, value]) => (
        <Paper key={key} sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            value={key}
            onChange={(e) => {
              const newPayload = { ...payload };
              delete newPayload[key];
              newPayload[e.target.value] = value;
              onChange(newPayload);
            }}
            label="Context Key"
          />
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
            onChange={(e) => {
              let parsedValue: any = e.target.value;
              try {
                // Try to parse as JSON for objects/arrays
                if (
                  e.target.value.trim().startsWith('{') ||
                  e.target.value.trim().startsWith('[')
                ) {
                  parsedValue = JSON.parse(e.target.value);
                }
              } catch {
                // Keep as string if not valid JSON
              }
              onChange({
                ...payload,
                [key]: parsedValue,
              });
            }}
            label="Context Value"
            placeholder="Value, multiline text, or JSON object"
          />
          <IconButton onClick={() => removeContextValue(key)} color="error">
            <DeleteIcon />
          </IconButton>
        </Paper>
      ))}

      {/* Add new context value */}
      <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          size="small"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          label="Context Key"
          placeholder="campaignType, settings.notifications"
        />
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          label="Context Value"
          placeholder='annual, true, {"key": "value"}, or multiline text'
        />
        <Button onClick={addContextValue} startIcon={<AddIcon />}>
          Add
        </Button>
      </Paper>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Nested Paths:</strong> Use dot notation like &ldquo;user.preferences.theme&rdquo;
          <br />
          <strong>JSON Values:</strong> Objects and arrays will be parsed automatically
        </Typography>
      </Alert>

      {/* Help Modal */}
      <SetContextHelpModal open={showHelpModal} onClose={() => setShowHelpModal(false)} />
    </Box>
  );
};
